import { useUser } from '@clerk/clerk-react'
import { useUserStore, isFreePlan } from '@/stores/user-store'
import { hasAccessToPlan, type NavItem } from '@/config/navigation'

/**
 * Hook to determine nav item access state and styling.
 * Shared between sidebar and mobile menu nav items.
 */
export function useNavItemAccess(item: NavItem) {
  const { isSignedIn, isLoaded: isClerkLoaded } = useUser()
  const { plan, isLoading: isUserLoading } = useUserStore()
  const isFree = isFreePlan(plan)

  // User data is loading - don't show plan-dependent UI yet
  const isLoading = !isClerkLoaded || isUserLoading

  // Wait for Clerk to load before showing special styling
  const showUpgradeGradient = isClerkLoaded && item.isSpecial && (isFree || !isSignedIn)

  // Only show feature gate crown after user data is loaded
  const showFeatureGate = !isLoading && item.requiresPlan && !hasAccessToPlan(plan, item.requiresPlan)

  // Show skeleton crown while loading for items that might need a crown
  const showSkeletonCrown = isLoading && item.requiresPlan

  // Use gold for starter tier, violet for creator/pro
  const isStarterFeature = item.requiresPlan === 'starter'
  const crownColor = isStarterFeature
    ? 'text-amber-400 drop-shadow-[0_0_4px_rgba(251,191,36,0.6)]'
    : 'text-violet-400 drop-shadow-[0_0_4px_rgba(139,92,246,0.6)]'

  // For the billing/upgrade button, show "Billing" only if signed in with a paid plan
  const label = item.isSpecial && isClerkLoaded && isSignedIn && !isFree ? 'Billing' : item.label

  // Should show auth modal instead of navigating when:
  // 1. User is not signed in AND (item requires a plan OR item is the upgrade button)
  const shouldShowAuthOnClick = isClerkLoaded && !isSignedIn && (item.requiresPlan || item.isSpecial)

  return {
    isSignedIn,
    isLoading,
    showUpgradeGradient,
    showFeatureGate,
    showSkeletonCrown,
    crownColor,
    label,
    shouldShowAuthOnClick,
  }
}
